import React, { useState, useEffect } from 'react';
import {
  Grid,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Link,
  CircularProgress,
  Alert
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { LoginCredentials } from '../../../types/auth';
import { NewPasswordRequiredError } from '../../../services/api/authService';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, loading: authLoading, error: authError, clearError } = useAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [credentials, setCredentials] = useState<LoginCredentials>({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);

  const message = location.state?.message;

  useEffect(() => {
    // Clear any existing errors on mount/unmount
    clearError();
    return () => clearError();
  }, [clearError]);

  // If there's a message passed via route state, clear it after 5s
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        navigate(location.pathname, { replace: true, state: {} });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message, navigate, location.pathname]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCredentials((prev) => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    setError('');
    clearError();
  };

  const validateForm = (): boolean => {
    if (!credentials.username || !credentials.password) {
      setError('All fields are required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    clearError();
    if (!validateForm()) return;

    setLoading(true);
    try {
      await signIn(credentials);
      const redirectTo = location.state?.from?.pathname || '/';
      navigate(redirectTo, { replace: true });
    } catch (err: any) {
      if (err instanceof NewPasswordRequiredError || err.name === 'NewPasswordRequiredError') {
        navigate('/new-password', {
          state: {
            challengeData: {
              session: err.session,
              challengeParameters: err.challengeParameters,
              username: credentials.username,
              password: credentials.password
            }
          }
        });
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const isLoading = loading || authLoading;
  const displayError = error || (authError?.message ?? '');

  return (
    <Grid
      container
      sx={{
        height: '100vh',
        backgroundColor: '#A46A3F', // Main background color
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Grid item xs={10} sm={8} md={4}>
        {/* The login card */}
        <Paper
          elevation={3}
          component="form"
          onSubmit={handleSubmit}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            borderRadius: 2
          }}
        >
          <Typography variant="h4" textAlign="center" sx={{ mb: 2 }}>
            LOGIN
          </Typography>

          {/* Optional message (e.g., success after sign-up) */}
          {message && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {message}
            </Alert>
          )}

          {/* Display error if any */}
          {displayError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {displayError}
            </Alert>
          )}

          {/* Username field */}
          <TextField
            required
            fullWidth
            label="Username"
            variant="outlined"
            name="username"
            value={credentials.username}
            onChange={handleChange}
            autoComplete="username"
          />

          {/* Password field with show/hide toggle */}
          <TextField
            required
            fullWidth
            label="Password"
            variant="outlined"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={credentials.password}
            onChange={handleChange}
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          {/* Remember Me & Forgot Password */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <FormControlLabel
              control={<Checkbox />}
              label="Remember me"
            />
            <Link component={RouterLink} to="/forgot-password" variant="body2">
              Forgot your password?
            </Link>
          </Box>

          {/* Sign In Button with darker button color */}
          <Button
            type="submit"
            variant="contained"
            sx={{
              backgroundColor: '#7A4F2D',
              color: '#fff',
              fontWeight: 'bold',
              '&:hover': { backgroundColor: '#6B4429' }
            }}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <CircularProgress size={20} sx={{ color: '#fff', mr: 1 }} />
                Signing in...
              </>
            ) : (
              'Sign in'
            )}
          </Button>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default Login;
